import type { TFunction } from 'i18next';
import type { FilterFieldOption } from '~/components/ui';
import {
  Currency,
  DeliveryMethod,
  LogisticsIncoterm,
  type TradePerspective,
} from '~/graphql/graphql';
import {
  newOrderFilterRowId,
  ORDER_FILTER_FIELD,
  type OrderFilterRow,
} from '~/pages/orders/lib/orderFilters';

type T = TFunction<'order'>;

export const DEFAULT_ORDER_FILTER_ROW: Omit<OrderFilterRow, 'id'> = {
  field: ORDER_FILTER_FIELD.purchaseOrderNumber,
  operator: 'contains',
  value: '',
  connector: 'and',
};

const textOperators = (t: T): FilterFieldOption['operatorOptions'] => [
  { label: t('order.filter.operators.contains'), operator: 'contains' },
  { label: t('order.filter.operators.is'), operator: 'eq' },
  { label: t('order.filter.operators.is_not'), operator: 'neq' },
  {
    label: t('order.filter.operators.does_not_contain'),
    operator: 'notContain',
  },
];

/**
 * The destination is matched against a concatenation of address columns, so an
 * exact comparison would require typing the whole concatenation. Only the
 * substring operators are offered.
 */
const destinationOperators = (t: T): FilterFieldOption['operatorOptions'] => [
  { label: t('order.filter.operators.contains'), operator: 'contains' },
  {
    label: t('order.filter.operators.does_not_contain'),
    operator: 'notContain',
  },
];

const dateOperators = (t: T): FilterFieldOption['operatorOptions'] => [
  { label: t('order.filter.operators.is'), operator: 'is' },
  { label: t('order.filter.operators.is_not'), operator: 'isNot' },
  { label: t('order.filter.operators.before'), operator: 'before' },
  { label: t('order.filter.operators.after'), operator: 'after' },
  { label: t('order.filter.operators.on_or_before'), operator: 'onOrBefore' },
  { label: t('order.filter.operators.on_or_after'), operator: 'onOrAfter' },
];

const numberOperators = (t: T): FilterFieldOption['operatorOptions'] => [
  { label: t('order.filter.operators.is'), operator: 'eq' },
  { label: t('order.filter.operators.is_not'), operator: 'neq' },
  { label: t('order.filter.operators.greater_than'), operator: 'gt' },
  { label: t('order.filter.operators.gte'), operator: 'gte' },
  { label: t('order.filter.operators.less_than'), operator: 'lt' },
  { label: t('order.filter.operators.lte'), operator: 'lte' },
];

const enumOperators = (t: T): FilterFieldOption['operatorOptions'] => [
  { label: t('order.filter.operators.is'), operator: 'eq' },
  { label: t('order.filter.operators.is_not'), operator: 'neq' },
];

const idOperators = (t: T): FilterFieldOption['operatorOptions'] => [
  { label: t('order.filter.operators.is'), operator: 'eq' },
];

/**
 * Incoterms and currency codes are the industry's own identifiers and are shown
 * verbatim; delivery methods are internal enum spellings and get translated.
 */
function codeOptions(values: readonly string[]) {
  return values.map((value) => ({ label: value, value }));
}

function deliveryMethodOptions(t: T) {
  return Object.values(DeliveryMethod).map((value) => ({
    label: t(`order.filter.delivery_methods.${value}`),
    value,
  }));
}

export function getOrderFilterFields(
  t: T,
  perspective: TradePerspective,
  createdByOptions: FilterFieldOption['selectOptions'] = []
): FilterFieldOption[] {
  const createdByField: FilterFieldOption[] =
    perspective === 'BUYING'
      ? [
          {
            field: ORDER_FILTER_FIELD.createdBy,
            label: t('order.filter.fields.created_by'),
            operatorOptions: idOperators(t),
            inputType: 'searchableSelect',
            selectOptions: createdByOptions,
          },
        ]
      : [];

  return [
    ...createdByField,
    {
      field: ORDER_FILTER_FIELD.purchaseOrderNumber,
      label: t('order.filter.fields.purchase_order_number'),
      operatorOptions: textOperators(t),
      inputType: 'string',
    },
    {
      field: ORDER_FILTER_FIELD.counterparty,
      label:
        perspective === 'BUYING'
          ? t('order.filter.fields.seller')
          : t('order.filter.fields.buyer'),
      operatorOptions: textOperators(t),
      inputType: 'string',
    },
    {
      field: ORDER_FILTER_FIELD.product,
      label: t('order.filter.fields.product'),
      operatorOptions: textOperators(t),
      inputType: 'string',
    },
    {
      field: ORDER_FILTER_FIELD.plant,
      label: t('order.filter.fields.plant'),
      operatorOptions: textOperators(t),
      inputType: 'string',
    },
    {
      field: ORDER_FILTER_FIELD.finalDeliveryLocation,
      label: t('order.filter.fields.final_delivery_location'),
      operatorOptions: destinationOperators(t),
      inputType: 'string',
    },
    {
      field: ORDER_FILTER_FIELD.orderDate,
      label: t('order.filter.fields.order_date'),
      operatorOptions: dateOperators(t),
      inputType: 'date',
    },
    {
      field: ORDER_FILTER_FIELD.shipDate,
      label: t('order.filter.fields.ship_date'),
      operatorOptions: dateOperators(t),
      inputType: 'date',
    },
    {
      field: ORDER_FILTER_FIELD.deliveryWindowStart,
      label: t('order.filter.fields.delivery_window_start'),
      operatorOptions: dateOperators(t),
      inputType: 'date',
    },
    {
      field: ORDER_FILTER_FIELD.deliveryWindowEnd,
      label: t('order.filter.fields.delivery_window_end'),
      operatorOptions: dateOperators(t),
      inputType: 'date',
    },
    {
      field: ORDER_FILTER_FIELD.total,
      label: t('order.filter.fields.total'),
      operatorOptions: numberOperators(t),
      inputType: 'number',
    },
    {
      field: ORDER_FILTER_FIELD.itemCount,
      label: t('order.filter.fields.item_count'),
      operatorOptions: numberOperators(t),
      inputType: 'number',
    },
    {
      field: ORDER_FILTER_FIELD.incoterm,
      label: t('order.filter.fields.incoterm'),
      operatorOptions: enumOperators(t),
      inputType: 'select',
      selectOptions: codeOptions(Object.values(LogisticsIncoterm)),
      withAllOption: true,
    },
    {
      field: ORDER_FILTER_FIELD.deliveryMethod,
      label: t('order.filter.fields.delivery_method'),
      operatorOptions: enumOperators(t),
      inputType: 'select',
      selectOptions: deliveryMethodOptions(t),
      withAllOption: true,
    },
    {
      field: ORDER_FILTER_FIELD.currency,
      label: t('order.filter.fields.currency'),
      operatorOptions: enumOperators(t),
      inputType: 'select',
      selectOptions: codeOptions(Object.values(Currency)),
      withAllOption: true,
    },
  ];
}

export function newOrderFilterRow(): OrderFilterRow {
  return { ...DEFAULT_ORDER_FILTER_ROW, id: newOrderFilterRowId() };
}
